import React, { useState } from "react";
import { Mail, Phone, MapPin, Award } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PublicLayout } from "../components/Layout";
import { api } from "../lib/api";

const REGISTRATIONS = [
  { k: "GST", v: "29ACPPC5869C1ZB" },
  { k: "IEC", v: "ACPPC5869C" },
  { k: "MSME", v: "UDYAM-KR-03-0251450" },
  { k: "TAN", v: "BLRM36977D" },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. Our team will respond within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="pt-28 md:pt-32 pb-12 md:pb-16 bg-[#F7F8F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44] mb-4">Contact</div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-[#222629] max-w-3xl leading-[1.05]">
            Let's talk steel.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-neutral-700 max-w-2xl leading-relaxed">
            Whether you need a partnership, a technical conversation, or a procurement discussion — we'd be glad to hear from you.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid lg:grid-cols-2 gap-10 md:gap-12">
          {/* Map + info */}
          <div>
            <div className="rounded-2xl overflow-hidden border border-neutral-200 h-72 md:h-80">
              <iframe
                data-testid="contact-map"
                title="R K STEEL Location"
                src="https://www.google.com/maps?q=12.9450551,77.5439064&hl=en&z=16&output=embed"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="mt-8 space-y-5">
              <a href="mailto:steel_rk@yahoo.com" data-testid="contact-email" className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-full bg-[#B8823E] text-[#12355B] flex items-center justify-center shrink-0"><Mail size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-heading">Email</div>
                  <div className="font-body text-[#222629] group-hover:text-[#12355B]">steel_rk@yahoo.com</div>
                </div>
              </a>
              <a href="tel:+919535516260" data-testid="contact-phone-1" className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-full bg-[#B8823E] text-[#12355B] flex items-center justify-center shrink-0"><Phone size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-heading">Phone</div>
                  <div className="font-body text-[#222629] group-hover:text-[#12355B]">+91 95355 16260</div>
                  <div className="font-body text-[#222629]">+91 90358 58364</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-[#B8823E] text-[#12355B] flex items-center justify-center shrink-0"><MapPin size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-500 font-heading">Headquarters</div>
                  <div className="font-body text-[#222629] leading-relaxed">
                    Ground Floor, No 29/3, 3rd Cross,<br />
                    Mysore Road, Pipe Lane,<br />
                    Kasturba Nagar, Bengaluru,<br />
                    Karnataka — 560026, India
                  </div>
                </div>
              </div>
            </div>

            {/* Certifications & Registrations */}
            <div className="mt-10 pt-8 border-t border-neutral-200">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-[#12355B] text-[#B8823E] flex items-center justify-center"><Award size={16} /></div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44]">Certified Trader</div>
                  <div className="font-heading text-[#222629] font-semibold">Registrations & Certifications</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {REGISTRATIONS.map(r => (
                  <div key={r.k} data-testid={`reg-${r.k.toLowerCase()}`} className="rounded-xl border border-neutral-200 p-4">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-neutral-500 font-heading">{r.k}</div>
                    <div className="font-mono text-[13px] text-[#222629] mt-1 wrap-anywhere">{r.v}</div>
                  </div>
                ))}
              </div>
              <Link to="/certifications" data-testid="view-certifications-link" className="text-sm text-[#12355B] hover:text-[#2F9E44] font-heading font-medium mt-6 inline-flex items-center gap-1 transition-colors">
                View all certifications →
              </Link>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#F7F8F5] rounded-2xl p-6 md:p-10 border border-neutral-200">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#222629] tracking-tight">Send us a message</h2>
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="enquiry-form">
              {[
                { name: "name", label: "Full Name*", type: "text", required: true },
                { name: "email", label: "Email*", type: "email", required: true },
                { name: "phone", label: "Phone", type: "tel" },
                { name: "company", label: "Company", type: "text" },
              ].map(f => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest text-neutral-500 font-heading">{f.label}</label>
                  <input
                    data-testid={`enq-input-${f.name}`}
                    type={f.type} required={f.required}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="mt-2 w-full border-b border-neutral-300 focus:border-[#12355B] outline-none py-2 bg-transparent"
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-neutral-500 font-heading">Subject*</label>
                <input
                  data-testid="enq-input-subject" required type="text" value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="mt-2 w-full border-b border-neutral-300 focus:border-[#12355B] outline-none py-2 bg-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-neutral-500 font-heading">Message*</label>
                <textarea
                  data-testid="enq-input-message" required rows={4} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full border-b border-neutral-300 focus:border-[#12355B] outline-none py-2 bg-transparent resize-none"
                />
              </div>
              <div className="md:col-span-2 flex justify-end pt-2">
                <button data-testid="enq-submit-btn" type="submit" disabled={submitting} className="btn-pill btn-primary disabled:opacity-50">
                  {submitting ? "Sending..." : "Send Enquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Contact;
